import { Text } from '@vkontakte/vkui';
import React, { type FC, memo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { aiPhotoGenerationPaidLimit } from 'reactApp/appHelpers/featuresHelpers/features/aiPhotoGeneration';
import { BaseConfirmDialog } from 'reactApp/components/BaseConfirmDialog/BaseConfirmDialog';
import { EButtonType } from 'reactApp/components/BaseConfirmDialog/BaseConformationDialog.types';
import { VK_UI_DARK_THEME } from 'reactApp/constants';
import { cancelImageGeneration } from 'reactApp/modules/aiPhotoGeneration/aiPhotoGeneration.actions';
import { useAttemptsLeft, useNextTime } from 'reactApp/modules/aiPhotoGeneration/aiPhotoGeneration.clamp.hooks';
import { getAiPhotoGenerationTime } from 'reactApp/modules/aiPhotoGeneration/aiPhotoGeneration.helpers';
import { useAiPhotoGenerationAnalyticData } from 'reactApp/modules/aiPhotoGeneration/aiPhotoGeneration.hooks';
import { getAiGenerationStartTime } from 'reactApp/modules/aiPhotoGeneration/aiPhotoGeneration.selectors';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';

import styles from './AiPhotoGenerationCancelGenerationModal.css';

export interface AiPhotoGenerationCancelGenerationModal {
    onClose?(): void;
}

const modal_type = 'generation-cancel';

export const AiPhotoGenerationCancelGenerationModal: FC<AiPhotoGenerationCancelGenerationModal> = memo(
    function AiPhotoGenerationCancelGenerationModal(props) {
        const { onClose } = props;

        const dispatch = useDispatch();

        const isCorp = useSelector(UserSelectors.isCorpUser);
        const isPaid = useSelector(UserSelectors.isPaidUser);
        const isCorpOrPaid = isCorp || isPaid;

        const startTime = useSelector(getAiGenerationStartTime);
        const analyticData = useAiPhotoGenerationAnalyticData();

        const { data: nextTime, isLoaded: isNextTimeLoaded } = useNextTime();
        const { data: attemptsLeft, isLoaded: isAttemptsLeftLoaded } = useAttemptsLeft();

        const renderHeader = useCallback(() => <h3 className={styles.title}>Остановить генерацию?</h3>, []);

        const renderContent = useCallback(() => {
            if (!isNextTimeLoaded || !isAttemptsLeftLoaded) {
                return;
            }

            if (isCorpOrPaid) {
                return (
                    <Text className={styles.description}>
                        У&nbsp;вас&nbsp;останется ещё&nbsp;{attemptsLeft}&nbsp;генераций из&nbsp;{aiPhotoGenerationPaidLimit}
                        &nbsp;на&nbsp;сутки, до&nbsp;{nextTime?.isTomorrow ? 'завтра, ' : ''}
                        {nextTime?.time}
                    </Text>
                );
            }

            return <Text className={styles.description}>Следующая генерация будет доступна через сутки, в&nbsp;{nextTime?.time}</Text>;
        }, [attemptsLeft, isAttemptsLeftLoaded, isCorpOrPaid, isNextTimeLoaded, nextTime]);

        const handleSuccess = useCallback(() => {
            dispatch(cancelImageGeneration());

            onClose?.();

            sendPaymentGa({
                eventCategory: ECategoryGa.aiPhotoGeneration,
                action: 'modal-click',
                modal_type,
                action_type: 'success',
                ...analyticData,
                generation_time: getAiPhotoGenerationTime(startTime),
                dwh_enabled: true,
                dwh_enabled_fields: [['paid_user', 'corp_user']],
            });
        }, [analyticData, dispatch, onClose, startTime]);

        const handleCancel = useCallback(() => {
            sendPaymentGa({
                eventCategory: ECategoryGa.aiPhotoGeneration,
                action: 'modal-click',
                modal_type,
                action_type: 'cancel',
                ...analyticData,
                generation_time: getAiPhotoGenerationTime(startTime),
                dwh_enabled: true,
                dwh_enabled_fields: [['paid_user', 'corp_user']],
            });
        }, [analyticData, startTime]);

        const handleDismiss = useCallback(() => {
            sendPaymentGa({
                eventCategory: ECategoryGa.aiPhotoGeneration,
                action: 'modal-close',
                modal_type,
                ...analyticData,
                generation_time: getAiPhotoGenerationTime(startTime),
                dwh_enabled: true,
                dwh_enabled_fields: [['paid_user', 'corp_user']],
            });
        }, [analyticData, startTime]);

        const handleShow = useCallback(() => {
            sendPaymentGa({
                eventCategory: ECategoryGa.aiPhotoGeneration,
                action: 'modal-show',
                modal_type,
                ...analyticData,
                generation_time: getAiPhotoGenerationTime(startTime),
                dwh_enabled: true,
                dwh_enabled_fields: [['paid_user', 'corp_user']],
            });
        }, [analyticData, startTime]);

        return (
            <BaseConfirmDialog
                size="small"
                dataQAId="ai-photo-gen-cancel-gen-modal"
                buttons={[EButtonType.accept, EButtonType.cancel]}
                successButtonText="Остановить"
                cancelButtonText="Не останавливать"
                renderContent={renderContent}
                renderHeader={renderHeader}
                onClose={onClose}
                onDismiss={handleDismiss}
                onSuccess={handleSuccess}
                onShow={handleShow}
                onCancel={handleCancel}
                isDarkTheme={true}
                className={VK_UI_DARK_THEME}
            />
        );
    }
);
